import { Controller, useFormContext } from 'react-hook-form'
import { CurecallMultiSelect, TOptionWithNumberValue } from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { useEffect, useMemo, useState } from 'react'
import { TChatbotGeneralFormValues } from './chatbot-form.type'
import { TMedEventUI } from 'src/types/med-event'
import { useFollowupIdFromParams } from '../../../_components/use-followup-id-from-params.hook'

export function ChatbotMedEventField() {
  const { t } = useTranslation()
  const {
    control,
    formState: { errors, defaultValues },
    setValue,
    clearErrors,
  } = useFormContext<TChatbotGeneralFormValues>()
  const followupId = useFollowupIdFromParams()
  const { getFollowupById, getMedEventLabelById } = useCurrentProject()
  const followup = getFollowupById(followupId)

  const MED_EVENTS_OPTIONS: TOptionWithNumberValue[] = useMemo(() => {
    return (
      followup?.configUI?.medEventsUI?.map((medEventUI: TMedEventUI) => ({
        value: medEventUI.MedEvent.id,
        label: getMedEventLabelById(medEventUI.MedEvent.id),
      })) || []
    )
  }, [followup, getMedEventLabelById])

  const [selectedMedEvents, setSelectedMedEvents] = useState<TOptionWithNumberValue[]>([])

  // Initialize selected med events from default values or auto-select if only one option
  useEffect(() => {
    if (selectedMedEvents.length > 0 || MED_EVENTS_OPTIONS.length === 0) return

    const medeventListIds = defaultValues?.schedule?.medeventListIds

    // If there are default values, use them
    if (medeventListIds && medeventListIds.length > 0) {
      setSelectedMedEvents(MED_EVENTS_OPTIONS.filter((medEvent) => medeventListIds.includes(medEvent.value)))
    }
    // Otherwise, if there's only one option, auto-select it
    else if (MED_EVENTS_OPTIONS.length === 1 && MED_EVENTS_OPTIONS[0]) {
      setSelectedMedEvents([MED_EVENTS_OPTIONS[0]])
      setValue('schedule.medeventListIds', [MED_EVENTS_OPTIONS[0].value])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [MED_EVENTS_OPTIONS])

  const handleChangeMedEvents = (options: TOptionWithNumberValue[]) => {
    setSelectedMedEvents(options)
    setValue(
      'schedule.medeventListIds',
      options.map((opt) => opt.value),
    )
    clearErrors('schedule.medeventListIds')
  }

  return (
    <Controller
      control={control}
      name="schedule.medeventListIds"
      rules={{ required: true }}
      render={() => (
        <CurecallMultiSelect
          label={t('pages.configuration.followups.chatbot.general-form.appointment-list')}
          isRequired={true}
          items={MED_EVENTS_OPTIONS}
          selectedItems={selectedMedEvents}
          onSelect={handleChangeMedEvents}
          selectedElementsLabel={`${selectedMedEvents.length} ${t('pages.configuration.followups.chatbot.general-form.lists-selected')}`}
          placeholder={t('pages.configuration.followups.chatbot.general-form.select-appointment-list')}
          tooltipContent={t('pages.configuration.followups.chatbot.general-form.appointment-list-tooltip')}
          tooltipPosition="right"
          wrapperClassName="c-w-[465px] c-relative"
          errorMessage={errors.schedule?.medeventListIds && t('common.form.required')}
        />
      )}
    />
  )
}
